import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'
import PropTypes from 'prop-types'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from '@nobia/zeus-components/lib/i18n/useTranslation'
import useAuth from '@nobia/zeus-components/lib/auth/useAuth'
import settings from '@nobia/zeus-components/lib/settings'
import { useAccount } from './AccountProvider'
import { useMagnetCustomerListingStoreBridge } from './MagnetCustomerListingStoreBridge'
import { useStaffMembers } from '../components/store'
import useCurrentStaffId from '../pages/messages/useCurrentStaffId'
import { useRoutes } from '../routes'
import { isCustomerListingPath } from '../utils/customerListingState'
import {
  insertUnassignedOption,
} from '../utils/designerViewOptions'
import { resolveDesignerEffectiveStoreId } from '../utils/resolveDesignerEffectiveStoreId'
import {
  buildDesignerViewSearch,
  parseDesignerViewStaffIdParam,
} from '../utils/designerViewSearchParams'
import { canonicalRoutePath } from '../utils/canonicalRoutePath'
import useMessagesInboxStaffFilter, {
  STAFF_FILTER_ALL,
  STAFF_FILTER_MY,
  getDefaultStaffFilterKey,
} from '../pages/messages/useMessagesInboxStaffFilter'

export const STAFF_FILTER_UNASSIGNED = '-1'

const DesignerViewContext = React.createContext(null)

const resolveStaffEmail = (staff) =>
  staff?.personalEmail?.trim() || staff?.email?.trim() || ''

/**
 * Maps shared designer view selection to listing "All customers" filter.staffId.
 */
export const mapDesignerViewToListingStaffId = (selectedKey, effectiveStaffId) => {
  if (selectedKey === STAFF_FILTER_ALL) {
    return ''
  }
  if (selectedKey === STAFF_FILTER_UNASSIGNED) {
    return STAFF_FILTER_UNASSIGNED
  }
  return effectiveStaffId != null ? String(effectiveStaffId) : ''
}

/**
 * Maps shared designer view to favourites search favouriteDesignerValue.
 */
export const mapDesignerViewToFavoriteDesignerValue = (
  selectedKey,
  selectedEmail
) => {
  if (selectedKey === STAFF_FILTER_ALL || selectedKey === STAFF_FILTER_UNASSIGNED) {
    return STAFF_FILTER_ALL
  }
  if (selectedKey === STAFF_FILTER_MY) {
    return STAFF_FILTER_MY
  }
  return selectedEmail || STAFF_FILTER_ALL
}

export const useDesignerView = () => {
  const ctx = useContext(DesignerViewContext)
  if (!ctx) {
    throw new Error('useDesignerView must be used within DesignerViewProvider')
  }
  return ctx
}

export const DesignerViewProvider = ({ children }) => {
  const { profile } = useAuth()
  const { store } = useAccount()
  const { isRegistered, listingStoreId } = useMagnetCustomerListingStoreBridge()
  const location = useLocation()
  const history = useHistory()
  const { t: tFilter } = useTranslation('pages.customers.filter')
  const {
    customers: customersRoute,
    dashboard: dashboardRoute,
  } = useRoutes()

  const isMagnetBrand = settings.brand === 'magnet'

  const urlSelectedKey = useMemo(() => {
    if (!isMagnetBrand) {
      return null
    }
    return parseDesignerViewStaffIdParam(location.search)
  }, [isMagnetBrand, location.search])

  const effectiveStoreId = useMemo(
    () =>
      resolveDesignerEffectiveStoreId({
        isRegistered,
        listingStoreId,
        accountStoreId: store?.id,
      }),
    [isRegistered, listingStoreId, store?.id]
  )

  const { staffId: ownStaffId, loading: ownStaffIdLoading } =
    useCurrentStaffId(effectiveStoreId)
  const { staffMembers, loading: staffMembersLoading } =
    useStaffMembers(effectiveStoreId)

  const staffListLoading = ownStaffIdLoading || staffMembersLoading
  const staffMembersLoadedForStoreRef = useRef(false)

  useEffect(() => {
    staffMembersLoadedForStoreRef.current = false
  }, [effectiveStoreId])

  useEffect(() => {
    if (!staffListLoading && effectiveStoreId) {
      staffMembersLoadedForStoreRef.current = true
    }
  }, [staffListLoading, effectiveStoreId, staffMembers.length])

  const {
    selectedKey,
    effectiveStaffId,
    staffFilterOptions,
    onStaffFilterChange,
    showActingBanner,
    actingStaffName,
    isStaffFilterReady,
  } = useMessagesInboxStaffFilter({
    storeId: effectiveStoreId,
    ownStaffId,
    staffMembers,
    staffListLoading,
    includeAllOption: true,
    includeMyOption: Boolean(ownStaffId),
    urlSelectedKey,
  })

  const syncDesignerViewUrl = useCallback(
    (key) => {
      if (!isMagnetBrand) {
        return
      }
      const nextSearch = buildDesignerViewSearch({
        selectedKey: key,
        existingSearch: location.search,
      })
      const canonical = canonicalRoutePath(location.pathname)
      const currentSearch = location.search || ''
      if (nextSearch !== currentSearch || canonical !== location.pathname) {
        history.replace({
          pathname: canonical,
          search: nextSearch,
        })
      }
    },
    [isMagnetBrand, history, location.pathname, location.search]
  )

  const handleStaffFilterChange = useCallback(
    (key) => {
      onStaffFilterChange(key)
      syncDesignerViewUrl(key)
    },
    [onStaffFilterChange, syncDesignerViewUrl]
  )

  const resetDesignerViewToDefault = useCallback(() => {
    const defaultKey = getDefaultStaffFilterKey(ownStaffId, {
      includeAllOption: true,
    })
    handleStaffFilterChange(defaultKey)
  }, [ownStaffId, handleStaffFilterChange])

  useEffect(() => {
    if (!isMagnetBrand || !isStaffFilterReady || selectedKey == null) {
      return
    }
    const urlKey = parseDesignerViewStaffIdParam(location.search)
    if (selectedKey === STAFF_FILTER_ALL) {
      if (location.search.includes('staffId=')) {
        syncDesignerViewUrl(STAFF_FILTER_ALL)
      }
      return
    }
    if (urlKey == null || String(urlKey) !== String(selectedKey)) {
      syncDesignerViewUrl(selectedKey)
    }
  }, [
    isMagnetBrand,
    isStaffFilterReady,
    selectedKey,
    location.pathname,
    location.search,
    syncDesignerViewUrl,
  ])

  const supportsUnassignedOption = useMemo(() => {
    const { pathname } = location
    if (pathname === dashboardRoute.path) {
      return true
    }
    return isCustomerListingPath(pathname, customersRoute.path)
  }, [location.pathname, dashboardRoute.path, customersRoute.path])

  const options = useMemo(() => {
    if (!supportsUnassignedOption) {
      return staffFilterOptions
    }
    return insertUnassignedOption(
      staffFilterOptions,
      tFilter('staff.unassigned')
    )
  }, [staffFilterOptions, supportsUnassignedOption, tFilter])

  const selectedLabel = useMemo(() => {
    if (selectedKey == null) {
      return ''
    }
    const match = options.find(
      (opt) => String(opt.value) === String(selectedKey)
    )
    return match?.label ?? ''
  }, [options, selectedKey])

  const profileEmail = profile?.email?.toLowerCase?.() || profile?.email || ''

  const selectedEmail = useMemo(() => {
    if (selectedKey === STAFF_FILTER_ALL || selectedKey === STAFF_FILTER_UNASSIGNED) {
      return ''
    }
    if (selectedKey === STAFF_FILTER_MY) {
      const ownStaff = staffMembers.find(
        (s) => String(s.id) === String(ownStaffId)
      )
      return (
        resolveStaffEmail(ownStaff)?.toLowerCase?.() ||
        profileEmail ||
        ''
      )
    }
    if (selectedKey != null && selectedKey !== '') {
      const staff = staffMembers.find(
        (s) => String(s.id) === String(selectedKey)
      )
      return resolveStaffEmail(staff)?.toLowerCase?.() || ''
    }
    return ''
  }, [selectedKey, staffMembers, ownStaffId, profileEmail])

  useLayoutEffect(() => {
    if (!isStaffFilterReady || staffListLoading) {
      return
    }
    if (
      selectedKey === STAFF_FILTER_UNASSIGNED &&
      !supportsUnassignedOption
    ) {
      resetDesignerViewToDefault()
    }
  }, [
    isStaffFilterReady,
    staffListLoading,
    selectedKey,
    supportsUnassignedOption,
    resetDesignerViewToDefault,
  ])

  useEffect(() => {
    if (!isStaffFilterReady || staffListLoading) {
      return
    }
    if (!effectiveStoreId) {
      return
    }
    if (!staffMembersLoadedForStoreRef.current) {
      return
    }
    if (
      selectedKey === STAFF_FILTER_ALL ||
      selectedKey === STAFF_FILTER_MY ||
      selectedKey === STAFF_FILTER_UNASSIGNED
    ) {
      return
    }
    const inStore = staffMembers.some(
      (s) => String(s.id) === String(selectedKey)
    )
    if (!inStore) {
      resetDesignerViewToDefault()
    }
  }, [
    isStaffFilterReady,
    staffListLoading,
    selectedKey,
    staffMembers,
    effectiveStoreId,
    resetDesignerViewToDefault,
  ])

  const value = useMemo(
    () => ({
      selectedKey,
      effectiveStaffId,
      selectedEmail,
      options,
      selectedLabel,
      onChange: handleStaffFilterChange,
      showActingBanner,
      actingStaffName,
      isReady: isStaffFilterReady,
      loading: staffListLoading,
      effectiveStoreId,
      ownStaffId,
      staffMembers,
      supportsUnassignedOption,
    }),
    [
      selectedKey,
      effectiveStaffId,
      selectedEmail,
      options,
      selectedLabel,
      handleStaffFilterChange,
      showActingBanner,
      actingStaffName,
      isStaffFilterReady,
      staffListLoading,
      effectiveStoreId,
      ownStaffId,
      staffMembers,
      supportsUnassignedOption,
    ]
  )

  useEffect(() => {
    if (location.pathname === '/') {
      return
    }
    const canonical = canonicalRoutePath(location.pathname)
    if (canonical !== location.pathname) {
      history.replace({
        pathname: canonical,
        search: location.search,
        state: location.state,
      })
    }
  }, [history, location.pathname, location.search, location.state])

  return (
    <DesignerViewContext.Provider value={value}>
      {children}
    </DesignerViewContext.Provider>
  )
}

DesignerViewProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
